import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../../config/axiosConfig';

export const createSalesTax = createAsyncThunk(
  'salesTaxes/create',
  async (taxData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/salestaxes/create`, taxData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchSalesTaxes = createAsyncThunk(
  'salesTaxes/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/salestaxes`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateSalesTax = createAsyncThunk(
  'salesTaxes/update',
  async ({ id, taxData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/salestaxes/update/${id}`, taxData);
      return response.data;
    } catch (error) {
      console.error('Update error:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteSalesTax = createAsyncThunk(
  'salesTaxes/delete',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/salestaxes/delete/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const initialState = {
  taxes: [],
  loading: false,
  error: null,
  success: false,
};

const salesTaxesSlice = createSlice({
  name: 'salesTaxes',
  initialState,
  reducers: {
    resetStatus: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder

    .addCase(createSalesTax.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSalesTax.fulfilled, (state, action) => {
        state.loading = false;
        state.taxes.push(action.payload);
        state.success = true;
      })
      .addCase(createSalesTax.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      
      .addCase(fetchSalesTaxes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSalesTaxes.fulfilled, (state, action) => {
        state.loading = false;
        // Ensure taxes is always an array; handle both raw array and wrapped { data/taxes } responses
        const payload = action.payload;
        const raw = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.taxes)
            ? payload.taxes
            : Array.isArray(payload?.data)
              ? payload.data
              : [];
        // Normalize so all consumers get taxName + taxPercentage (backend may return tax_name, tax_percentage)
        state.taxes = raw.map((t) => ({
          ...t,
          taxName: t.taxName ?? t.tax_name ?? '',
          taxPercentage: t.taxPercentage ?? t.tax_percentage,
        }));
      })
      .addCase(fetchSalesTaxes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      
      .addCase(updateSalesTax.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSalesTax.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.taxes.findIndex(tax => tax._id === action.payload._id);
        if (index !== -1) {
          state.taxes[index] = action.payload;
        }
        state.success = true;
      })
      .addCase(updateSalesTax.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      
      .addCase(deleteSalesTax.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSalesTax.fulfilled, (state, action) => {
        state.loading = false;
        state.taxes = state.taxes.filter(tax => tax._id !== action.payload);
        state.success = true;
      })
      .addCase(deleteSalesTax.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      });
  },
});

export const { resetStatus } = salesTaxesSlice.actions;
export default salesTaxesSlice.reducer;